/**
 * GH-877: a saved widget's profile / sign-in option, in the give box.
 *
 * Off (the default, and every widget saved before GH-877) the box is exactly the guest box it has
 * always been: no profile button, no stored session read, nothing in storage. On, it shows a
 * profile button and — on the card form, to a donor who is not signed in — a sign-in offer.
 * Signing in never changes what a gift is or costs; it sends the donor's session with the payment
 * so the gift lands in their own receipts (the server links it, best effort, exactly as on the
 * ordinary give page — `POST /v1/donations/payment-intents`). Giving never needs an account.
 *
 * ## Where the sign-in happens
 *
 * - **At top level** (the copied link, an email button's tab, the button's small window) the box is
 *   first-party: it reads the device's remembered session (checked with the server first, so a
 *   stale one never reads as "signed in") and signs in with the one sign-in sheet every donor page
 *   uses (`SignInSheet`: Apple, Google, phone).
 * - **In a frame on somebody else's site** storage is partitioned and Apple's and Google's sign-in
 *   cannot finish inside the frame, so the box opens a small GiveHere window
 *   (`/embed/give/<org>/sign-in`, never framable) that signs in at top level and hands the session
 *   back — to its opener only, on this origin only, with the nonce this box put in its address
 *   (`embed-frame.ts`). The framed box keeps that session in memory only: nothing is written to
 *   the partitioned storage, and a reload signs the donor out of that frame.
 */
import { EMBED_WIDGET_COPY, embedSignInPath } from "@givehere/contracts";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  DonorApiError,
  donorSignOut,
  fetchDonorSession,
  forgetToken,
  storedToken,
} from "../donor-session.js";
import { PersonIcon } from "../icons.js";
import { SignInSheet } from "../SignInSheet.js";
import { isFramed, isSignedInMessage } from "./embed-frame.js";

const COPY = EMBED_WIDGET_COPY.donor;
const WINDOW_FEATURES = "popup,width=440,height=680";

export type EmbedDonor = {
  /** Whether the widget shows the profile / sign-in option at all. */
  readonly enabled: boolean;
  /** The donor's session, sent with the quote and the payment; null for a guest. */
  readonly token: string | null;
  /** A sentence for the box's status line after signing in or out; null otherwise. */
  readonly notice: string | null;
  /** The sign-in window's address when the browser blocked it, to open by hand. */
  readonly blockedUrl: string | null;
  /** Start signing in. Call inside the tap: a framed box opens its window before anything waits. */
  readonly signIn: (onSignedIn?: () => void) => void;
  readonly signOut: () => void;
  /** The sign-in sheet, while it is open at top level; null otherwise. */
  readonly sheet: ReactNode;
};

const randomNonce = (): string =>
  Array.from(globalThis.crypto.getRandomValues(new Uint8Array(18)), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

export function useEmbedDonor(input: {
  readonly enabled: boolean;
  readonly organizationId: string;
  /** The console's preview: drawn, never acted on. */
  readonly preview: boolean;
}): EmbedDonor {
  const { enabled, organizationId, preview } = input;
  const [token, setToken] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const popup = useRef<Window | null>(null);
  const nonce = useRef<string | null>(null);
  const after = useRef<(() => void) | undefined>(undefined);

  const signedIn = (session: string) => {
    setToken(session);
    // From the card form, the form closes: say what to do next. Otherwise, what signing in did.
    setNotice(after.current ? COPY.signedInContinue : COPY.signedInNote);
    setBlockedUrl(null);
    const next = after.current;
    after.current = undefined;
    next?.();
  };
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;

  // At top level, a session this device already remembers — once the server says it is current.
  useEffect(() => {
    if (!enabled || preview || isFramed()) return;
    const remembered = storedToken();
    if (!remembered) return;
    let cancelled = false;
    fetchDonorSession(remembered)
      .then(() => {
        if (!cancelled) setToken(remembered);
      })
      .catch((caught: unknown) => {
        // A session the server no longer honours is forgotten; a network blip just stays guest.
        if (caught instanceof DonorApiError && caught.status === 401) forgetToken();
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, preview]);

  // A framed box: the sign-in window's answer — this origin, our window, our nonce.
  useEffect(() => {
    if (!enabled || preview) return;
    const listen = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || nonce.current === null) return;
      // Only the sign-in window's own answer is considered at all, so the Apple Pay window's
      // messages are never mistaken for it (and never adopted as it).
      if (!isSignedInMessage(event.data, nonce.current)) return;
      if (!popup.current) {
        // Opened from the "blocked" link: adopt it only if this box is its opener.
        const source = event.source as Window | null;
        try {
          if (source && source !== window && source.opener === window) popup.current = source;
        } catch {
          return;
        }
      }
      if (event.source !== popup.current) return;
      nonce.current = null;
      popup.current = null;
      signedInRef.current(event.data.token);
    };
    window.addEventListener("message", listen);
    return () => window.removeEventListener("message", listen);
  }, [enabled, preview]);

  const signIn = (onSignedIn?: () => void) => {
    if (!enabled || preview) return;
    after.current = onSignedIn;
    setNotice(null);
    if (!isFramed()) {
      setSheetOpen(true);
      return;
    }
    const fresh = randomNonce();
    nonce.current = fresh;
    const url = new URL(embedSignInPath(organizationId), window.location.origin);
    url.hash = `n=${fresh}`;
    // `_blank`, not a named window: a name could match a window some other page already opened.
    const opened = window.open(url.toString(), "_blank", WINDOW_FEATURES);
    popup.current = opened;
    setBlockedUrl(opened ? null : url.toString());
  };

  const signOut = () => {
    const current = token;
    setToken(null);
    setNotice(null);
    if (!current) return;
    // Framed or not, the session itself is ended, so "Sign out" means signed out on this device
    // too (the sign-in window remembered it in GiveHere's own storage).
    void donorSignOut(current).catch(() => undefined);
    if (!isFramed()) forgetToken();
  };

  const sheet = sheetOpen ? (
    <SignInSheet
      heading={COPY.windowHeading}
      lede={COPY.windowLede}
      onIssued={(issued) => {
        setSheetOpen(false);
        signedInRef.current(issued.token);
      }}
      onDismiss={() => {
        after.current = undefined;
        setSheetOpen(false);
      }}
    />
  ) : null;

  return { enabled, token, notice, blockedUrl, signIn, signOut, sheet };
}

/**
 * The profile button at the inline end of the box's first line: "Sign in" for a guest, "Your
 * profile" (with the quiet dot the donor pages use) once signed in, where it opens a one-line
 * panel to sign out. In the console's preview it is drawn and does nothing, like the pay button.
 */
export function EmbedProfileButton({ donor, preview }: { donor: EmbedDonor; preview: boolean }) {
  const [open, setOpen] = useState(false);
  const signedIn = donor.token !== null;
  return (
    <>
      <button
        type="button"
        className={
          signedIn
            ? "org__corner org__corner--profile org__corner--in"
            : "org__corner org__corner--profile"
        }
        aria-label={signedIn ? COPY.profileSignedInLabel : COPY.profileSignedOutLabel}
        aria-expanded={signedIn ? open : undefined}
        aria-disabled={preview || undefined}
        onClick={() => {
          if (preview) return;
          if (signedIn) setOpen(!open);
          else donor.signIn();
        }}
      >
        <PersonIcon />
        {signedIn && <span className="home__profile-dot" aria-hidden="true" />}
      </button>
      {signedIn && open && (
        <div className="embed__signed-in">
          <span>{COPY.signedInNote}</span>
          <button
            type="button"
            className="link"
            onClick={() => {
              setOpen(false);
              donor.signOut();
            }}
          >
            {COPY.signOutLabel}
          </button>
        </div>
      )}
    </>
  );
}

/** The card form's offer, for a donor who is not signed in. */
export function EmbedSignInOffer({
  donor,
  onSignedIn,
}: {
  donor: EmbedDonor;
  onSignedIn: () => void;
}) {
  if (!donor.enabled || donor.token !== null) return null;
  return (
    <div className="embed__offer">
      <button type="button" className="link link--center" onClick={() => donor.signIn(onSignedIn)}>
        {COPY.offerLabel}
      </button>
      <p className="embed__note">{COPY.offerNote}</p>
    </div>
  );
}

/** "Your browser blocked the sign-in window" with a link to open it by hand. */
export function EmbedSignInBlocked({ donor }: { donor: EmbedDonor }) {
  if (!donor.blockedUrl) return null;
  return (
    <p className="embed__note">
      {COPY.windowBlocked}{" "}
      <a href={donor.blockedUrl} target="_blank" rel="opener">
        {COPY.windowBlockedLink}
      </a>
      .
    </p>
  );
}
